/**
 * TriggerStudio — modal full-screen para editar canales + contenido +
 * triggers de una campaña.
 *
 * Layout:
 *   ┌─ Header sticky con título + botón Cerrar + Guardar
 *   ├─ Tab nav: [Canales y mensajes] [Triggers]
 *   └─ Tab content scroll-able
 *
 * Tabs:
 *  1. Canales y mensajes
 *     - 3 toggles (Email / WhatsApp / Push)
 *     - Para cada uno seleccionado: editor inline con subject, body, etc.
 *
 *  2. Triggers
 *     - Toggle global "Cumplir TODOS / CUALQUIERA" (AND/OR)
 *     - Lista de triggers cada uno con su editor según type
 *     - "+ Agregar trigger" → modal de selección de tipo
 *
 * Guardar persiste todo en un solo flow (channels+content + triggers).
 */

import { useEffect, useMemo, useState } from "react";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import {
    X,
    Save,
    Mail,
    MessageCircle,
    Bell,
    Plus,
    Trash2,
    Sparkles,
    Loader2,
    ChevronRight,
    Calendar,
    Hash,
    AlertCircle,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { objectivesApi } from "../api";
import {
    EVENT_CATALOG,
    EVENTS_BY_CATEGORY,
    EVENT_BY_KEY,
    CATEGORY_META,
    WA_MESSAGE_CATEGORIES,
    type EventCategory,
} from "../event-catalog";
import type {
    CampaignChannel,
    CampaignContent,
    EmailContent,
    Objective,
    PushContent,
    Trigger,
    TriggerGroup,
    WhatsAppContent,
    WhatsAppMessageCategory,
} from "../types";

interface TriggerStudioProps {
    campaign: Objective;
    onClose: () => void;
    /** Llamado después de guardar exitosamente — el padre debe refetch */
    onSaved: (updated: Objective) => void;
}

type Tab = "channels" | "triggers";

export default function TriggerStudio({
    campaign,
    onClose,
    onSaved,
}: TriggerStudioProps) {
    const [tab, setTab] = useState<Tab>("channels");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const modalRef = useFocusTrap<HTMLDivElement>(true);

    // ─── Canales + contenido ───
    const [channels, setChannels] = useState<CampaignChannel[]>(
        campaign.channels || [],
    );
    const [content, setContent] = useState<CampaignContent>(
        campaign.content || {},
    );

    // ─── Triggers ───
    const [triggers, setTriggers] = useState<TriggerGroup>(
        campaign.triggers || { logic: "AND", items: [] },
    );

    /* ESC + scroll lock */
    useEffect(() => {
        const esc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", esc);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", esc);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    const dirty = useMemo(() => {
        return (
            JSON.stringify(channels) !==
                JSON.stringify(campaign.channels || []) ||
            JSON.stringify(content) !==
                JSON.stringify(campaign.content || {}) ||
            JSON.stringify(triggers) !==
                JSON.stringify(
                    campaign.triggers || { logic: "AND", items: [] },
                )
        );
    }, [channels, content, triggers, campaign]);

    const handleSave = async () => {
        if (!dirty || saving) return;
        setSaving(true);
        setError(null);

        // Guardar canales + content
        const r1 = await objectivesApi.updateChannels(campaign.id, {
            channels,
            content,
        });
        if (!r1.success) {
            setError(r1.error || "Error al guardar canales");
            setSaving(false);
            return;
        }

        // Guardar triggers
        const r2 = await objectivesApi.updateTriggers(campaign.id, triggers);
        if (!r2.success) {
            setError(r2.error || "Error al guardar triggers");
            setSaving(false);
            return;
        }

        setSaving(false);
        onSaved(r2.data);
    };

    return (
        <>
            <div
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 animate-fade-in"
                onClick={onClose}
            />
            <div
                ref={modalRef}
                className="fixed inset-4 md:inset-8 lg:inset-12 bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden animate-zoom-in"
                role="dialog"
                aria-modal="true"
                aria-labelledby="trigger-studio-title"
            >
                {/* Header sticky */}
                <header className="px-6 py-4 border-b border-slate-100 flex-shrink-0 flex items-center justify-between gap-4 bg-white">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center flex-shrink-0">
                            <Sparkles size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 id="trigger-studio-title" className="text-base font-bold text-slate-900 truncate">
                                {campaign.emoji} {campaign.name}
                            </h2>
                            <p className="text-[11px] text-slate-500">
                                Editar canales, contenido y triggers
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {dirty && (
                            <span className="text-xs text-amber-600 font-medium hidden sm:inline">
                                Cambios sin guardar
                            </span>
                        )}
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={!dirty || saving}
                        >
                            {saving ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Save size={14} />
                            )}
                            Guardar
                        </Button>
                        <button
                            onClick={onClose}
                            className="w-9 h-9 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
                            aria-label="Cerrar"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </header>

                {/* Tab nav */}
                <nav className="border-b border-slate-100 flex-shrink-0 bg-white">
                    <div className="px-6 flex items-center gap-1">
                        {[
                            {
                                key: "channels" as Tab,
                                label: "Canales y mensajes",
                                icon: Mail,
                                count: channels.length,
                            },
                            {
                                key: "triggers" as Tab,
                                label: "Triggers",
                                icon: Sparkles,
                                count: triggers.items.length,
                            },
                        ].map((t) => {
                            const Icon = t.icon;
                            const active = tab === t.key;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setTab(t.key)}
                                    className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                                        active
                                            ? "text-violet-700"
                                            : "text-slate-500 hover:text-slate-800"
                                    }`}
                                >
                                    <Icon size={15} />
                                    {t.label}
                                    {t.count > 0 && (
                                        <span
                                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                                active
                                                    ? "bg-violet-100 text-violet-700"
                                                    : "bg-slate-200 text-slate-600"
                                            }`}
                                        >
                                            {t.count}
                                        </span>
                                    )}
                                    {active && (
                                        <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-violet-600 rounded-t-full" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </nav>

                {/* Tab content */}
                <div className="flex-1 overflow-y-auto bg-slate-50/30">
                    {error && (
                        <div className="mx-6 mt-4 flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl text-red-700">
                            <AlertCircle size={16} />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    {tab === "channels" && (
                        <ChannelsTab
                            channels={channels}
                            content={content}
                            onChangeChannels={setChannels}
                            onChangeContent={setContent}
                        />
                    )}
                    {tab === "triggers" && (
                        <TriggersTab
                            triggers={triggers}
                            onChange={setTriggers}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

/* ──────────────────────────────────────────────────────────────────────
 *  TAB 1 — CANALES Y MENSAJES
 * ────────────────────────────────────────────────────────────────────── */

const CHANNEL_META: Record<
    CampaignChannel,
    {
        label: string;
        emoji: string;
        icon: typeof Mail;
        description: string;
        gradient: string;
    }
> = {
    email: {
        label: "Email",
        emoji: "📧",
        icon: Mail,
        description: "Subject + body — para mensajes largos o formales",
        gradient: "from-blue-500 to-cyan-500",
    },
    whatsapp: {
        label: "WhatsApp",
        emoji: "💬",
        icon: MessageCircle,
        description: "Texto conversacional + media opcional",
        gradient: "from-emerald-500 to-green-500",
    },
    push: {
        label: "Push notification",
        emoji: "🔔",
        icon: Bell,
        description: "Title + body cortos — perfecto para impulsos",
        gradient: "from-violet-500 to-fuchsia-500",
    },
};

function ChannelsTab({
    channels,
    content,
    onChangeChannels,
    onChangeContent,
}: {
    channels: CampaignChannel[];
    content: CampaignContent;
    onChangeChannels: (c: CampaignChannel[]) => void;
    onChangeContent: (c: CampaignContent) => void;
}) {
    const toggleChannel = (ch: CampaignChannel) => {
        if (channels.includes(ch)) {
            onChangeChannels(channels.filter((c) => c !== ch));
        } else {
            onChangeChannels([...channels, ch]);
            // Inicializar contenido vacío si no existía
            if (!content[ch]) {
                const init: CampaignContent = { ...content };
                if (ch === "email")
                    init.email = { subject: "", body: "" };
                if (ch === "whatsapp") init.whatsapp = { text: "" };
                if (ch === "push") init.push = { title: "", body: "" };
                onChangeContent(init);
            }
        }
    };

    const updateEmail = (patch: Partial<EmailContent>) => {
        onChangeContent({
            ...content,
            email: { ...(content.email || { subject: "", body: "" }), ...patch },
        });
    };
    const updateWA = (patch: Partial<WhatsAppContent>) => {
        onChangeContent({
            ...content,
            whatsapp: { ...(content.whatsapp || { text: "" }), ...patch },
        });
    };
    const updatePush = (patch: Partial<PushContent>) => {
        onChangeContent({
            ...content,
            push: {
                ...(content.push || { title: "", body: "" }),
                ...patch,
            },
        });
    };

    return (
        <div className="p-6 space-y-5 max-w-4xl mx-auto">
            {/* Section: pick channels */}
            <section>
                <h3 className="text-sm font-bold text-slate-900 mb-1">
                    ¿Por dónde sale esta campaña?
                </h3>
                <p className="text-xs text-slate-500 mb-3">
                    Podés combinar canales — cada uno tiene su propio mensaje
                    adaptado al formato.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {(["email", "whatsapp", "push"] as CampaignChannel[]).map(
                        (ch) => {
                            const meta = CHANNEL_META[ch];
                            const Icon = meta.icon;
                            const selected = channels.includes(ch);
                            return (
                                <button
                                    key={ch}
                                    onClick={() => toggleChannel(ch)}
                                    className={`relative p-4 rounded-2xl border-2 text-left transition-all ${
                                        selected
                                            ? "border-violet-400 bg-violet-50/40 shadow-sm"
                                            : "border-slate-200 bg-white hover:border-slate-300"
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div
                                            className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.gradient} text-white flex items-center justify-center flex-shrink-0`}
                                        >
                                            <Icon size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-bold text-slate-900 text-sm">
                                                    {meta.label}
                                                </h4>
                                                {selected && (
                                                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-600 text-white">
                                                        ON
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                                                {meta.description}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            );
                        },
                    )}
                </div>
            </section>

            {/* Per-channel editors */}
            {channels.length === 0 && !content.email && !content.whatsapp && !content.push && (
                <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center">
                    <Sparkles
                        size={28}
                        className="mx-auto text-slate-300 mb-2"
                    />
                    <p className="text-sm text-slate-500">
                        Elegí al menos un canal arriba para empezar a editar el
                        contenido.
                    </p>
                </div>
            )}

            {(channels.includes("email") || !!content.email) && (
                <ChannelEditor
                    channel="email"
                    title="Email"
                    icon={<Mail size={15} />}
                    gradient={CHANNEL_META.email.gradient}
                    inactive={!channels.includes("email")}
                    onReactivate={() => toggleChannel("email")}
                >
                    <Field
                        label="Asunto"
                        sub="Lo que ven en la bandeja antes de abrir"
                        maxLength={80}
                        value={content.email?.subject || ""}
                        onChange={(v) => updateEmail({ subject: v })}
                        disabled={!channels.includes("email")}
                    />
                    <Field
                        label="Preheader"
                        sub="Línea de preview (opcional)"
                        maxLength={120}
                        value={content.email?.preheader || ""}
                        onChange={(v) => updateEmail({ preheader: v })}
                        optional
                        disabled={!channels.includes("email")}
                    />
                    <Field
                        label="Cuerpo del mensaje"
                        multiline
                        rows={8}
                        value={content.email?.body || ""}
                        onChange={(v) => updateEmail({ body: v })}
                        placeholder="Hola {{nombre}}, ..."
                        disabled={!channels.includes("email")}
                    />
                </ChannelEditor>
            )}

            {(channels.includes("whatsapp") || !!content.whatsapp) && (
                <ChannelEditor
                    channel="whatsapp"
                    title="WhatsApp"
                    icon={<MessageCircle size={15} />}
                    gradient={CHANNEL_META.whatsapp.gradient}
                    inactive={!channels.includes("whatsapp")}
                    onReactivate={() => toggleChannel("whatsapp")}
                >
                    <Field
                        label="Mensaje"
                        sub="Conversacional, corto, con emoji 1-2 max"
                        multiline
                        rows={5}
                        value={content.whatsapp?.text || ""}
                        onChange={(v) => updateWA({ text: v })}
                        placeholder="Hola {{nombre}} 👋 ..."
                        disabled={!channels.includes("whatsapp")}
                    />
                    <Field
                        label="URL de imagen/media"
                        sub="Imagen o video que acompaña (opcional)"
                        value={content.whatsapp?.mediaUrl || ""}
                        onChange={(v) => updateWA({ mediaUrl: v })}
                        placeholder="https://…"
                        optional
                        disabled={!channels.includes("whatsapp")}
                    />
                </ChannelEditor>
            )}

            {(channels.includes("push") || !!content.push) && (
                <ChannelEditor
                    channel="push"
                    title="Push notification"
                    icon={<Bell size={15} />}
                    gradient={CHANNEL_META.push.gradient}
                    inactive={!channels.includes("push")}
                    onReactivate={() => toggleChannel("push")}
                >
                    <Field
                        label="Título"
                        sub="Hasta 50 caracteres — lo que se ve en el lock screen"
                        maxLength={50}
                        value={content.push?.title || ""}
                        onChange={(v) => updatePush({ title: v })}
                        disabled={!channels.includes("push")}
                    />
                    <Field
                        label="Cuerpo"
                        sub="Hasta 140 caracteres — corto y directo"
                        multiline
                        rows={2}
                        maxLength={140}
                        value={content.push?.body || ""}
                        onChange={(v) => updatePush({ body: v })}
                        disabled={!channels.includes("push")}
                    />
                    <Field
                        label="Imagen (URL)"
                        value={content.push?.imageUrl || ""}
                        onChange={(v) => updatePush({ imageUrl: v })}
                        placeholder="https://…"
                        optional
                        disabled={!channels.includes("push")}
                    />
                    <Field
                        label="Deep link al tap"
                        value={content.push?.deepLink || ""}
                        onChange={(v) => updatePush({ deepLink: v })}
                        placeholder="deenex://carrito"
                        optional
                        disabled={!channels.includes("push")}
                    />
                </ChannelEditor>
            )}
        </div>
    );
}

/* ─── Sub: editor envoltorio por canal ─── */
function ChannelEditor({
    title,
    icon,
    gradient,
    inactive,
    onReactivate,
    children,
}: {
    channel: CampaignChannel;
    title: string;
    icon: React.ReactNode;
    gradient: string;
    inactive?: boolean;
    onReactivate?: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden transition-opacity duration-200${inactive ? " opacity-50" : ""}`}>
            <div
                className={`px-5 py-3 bg-gradient-to-r ${gradient} text-white flex items-center justify-between gap-2`}
            >
                <div className="flex items-center gap-2">
                    {icon}
                    <h4 className="font-bold text-sm">{title}</h4>
                </div>
                {inactive && onReactivate && (
                    <button
                        onClick={onReactivate}
                        className="text-[11px] font-semibold text-white/90 bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded-full transition-colors"
                    >
                        Canal desactivado — reactivar →
                    </button>
                )}
            </div>
            <div className="p-5 space-y-4">{children}</div>
        </div>
    );
}

/* ─── Sub: campo con label + textarea/input ─── */
function Field({
    label,
    sub,
    value,
    onChange,
    multiline,
    rows = 1,
    maxLength,
    placeholder,
    optional,
    disabled,
}: {
    label: string;
    sub?: string;
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    rows?: number;
    maxLength?: number;
    placeholder?: string;
    optional?: boolean;
    disabled?: boolean;
}) {
    return (
        <div>
            <div className="flex items-baseline justify-between mb-1">
                <label className="text-xs font-semibold text-slate-700">
                    {label}
                    {optional && (
                        <span className="text-slate-400 ml-1 font-normal">
                            (opcional)
                        </span>
                    )}
                </label>
                {maxLength && (
                    <span
                        className={`text-[10px] font-mono ${
                            value.length > maxLength * 0.9
                                ? "text-amber-600"
                                : "text-slate-400"
                        }`}
                    >
                        {value.length}/{maxLength}
                    </span>
                )}
            </div>
            {sub && <p className="text-[11px] text-slate-500 mb-1.5">{sub}</p>}
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => {
                        onChange(
                            maxLength
                                ? e.target.value.slice(0, maxLength)
                                : e.target.value,
                        );
                        // Auto-resize
                        e.target.style.height = "auto";
                        e.target.style.height = e.target.scrollHeight + "px";
                    }}
                    rows={rows}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all overflow-hidden${disabled ? " bg-slate-50 cursor-not-allowed" : ""}`}
                />
            ) : (
                <input
                    type="text"
                    value={value}
                    onChange={(e) =>
                        onChange(
                            maxLength
                                ? e.target.value.slice(0, maxLength)
                                : e.target.value,
                        )
                    }
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all${disabled ? " bg-slate-50 cursor-not-allowed" : ""}`}
                />
            )}
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────
 *  TAB 2 — TRIGGERS
 * ────────────────────────────────────────────────────────────────────── */

function TriggersTab({
    triggers,
    onChange,
}: {
    triggers: TriggerGroup;
    onChange: (g: TriggerGroup) => void;
}) {
    const [adding, setAdding] = useState(false);

    const setLogic = (logic: "AND" | "OR") =>
        onChange({ ...triggers, logic });

    const addTrigger = (t: Trigger) => {
        onChange({ ...triggers, items: [...triggers.items, t] });
        setAdding(false);
    };

    const updateTrigger = (idx: number, patch: Partial<Trigger>) => {
        const next = [...triggers.items];
        // @ts-expect-error — discriminated union update
        next[idx] = { ...next[idx], ...patch };
        onChange({ ...triggers, items: next });
    };

    const removeTrigger = (idx: number) => {
        onChange({
            ...triggers,
            items: triggers.items.filter((_, i) => i !== idx),
        });
    };

    return (
        <div className="p-6 space-y-4 max-w-4xl mx-auto">
            {/* Logic toggle */}
            {triggers.items.length > 1 && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-3">
                    <span className="text-xs font-semibold text-slate-700">
                        La campaña dispara cuando se cumplen:
                    </span>
                    <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                        {(["AND", "OR"] as const).map((logic) => {
                            const active = triggers.logic === logic;
                            return (
                                <button
                                    key={logic}
                                    onClick={() => setLogic(logic)}
                                    className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                                        active
                                            ? "bg-violet-600 text-white shadow-sm"
                                            : "text-slate-600 hover:text-slate-900"
                                    }`}
                                >
                                    {logic === "AND"
                                        ? "TODOS"
                                        : "CUALQUIERA"}
                                </button>
                            );
                        })}
                    </div>
                    <span className="text-[11px] text-slate-500">
                        {triggers.logic === "AND"
                            ? "Intersección — los 2+ se deben dar a la vez"
                            : "Cualquiera de los triggers dispara la campaña"}
                    </span>
                </div>
            )}

            {/* Lista de triggers */}
            <div className="space-y-3">
                {triggers.items.map((t, idx) => (
                    <div key={t.id || idx}>
                        {idx > 0 && (
                            <div className="flex items-center my-2">
                                <div className="flex-1 h-px bg-slate-200" />
                                <span className="px-3 text-[10px] font-bold tracking-widest text-slate-400 bg-slate-50">
                                    {triggers.logic === "AND" ? "Y" : "O"}
                                </span>
                                <div className="flex-1 h-px bg-slate-200" />
                            </div>
                        )}
                        <TriggerCard
                            trigger={t}
                            onChange={(patch) => updateTrigger(idx, patch)}
                            onRemove={() => removeTrigger(idx)}
                        />
                    </div>
                ))}
            </div>

            {/* Empty state */}
            {triggers.items.length === 0 && !adding && (
                <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center">
                    <Sparkles
                        size={28}
                        className="mx-auto text-slate-300 mb-2"
                    />
                    <p className="text-sm text-slate-700 font-medium mb-1">
                        Esta campaña aún no tiene triggers
                    </p>
                    <p className="text-xs text-slate-500 mb-4 max-w-md mx-auto">
                        Sin trigger, la campaña queda en draft. Agregá al menos
                        uno para que arranque a correr (evento, fecha, o
                        mensaje de WhatsApp entrante).
                    </p>
                </div>
            )}

            {/* Add trigger */}
            {adding ? (
                <AddTriggerSheet
                    onCancel={() => setAdding(false)}
                    onAdd={addTrigger}
                />
            ) : (
                <button
                    onClick={() => setAdding(true)}
                    className="w-full p-4 rounded-2xl border-2 border-dashed border-slate-300 hover:border-violet-400 hover:bg-violet-50/40 transition-all text-sm font-semibold text-slate-600 hover:text-violet-700 flex items-center justify-center gap-2"
                >
                    <Plus size={16} />
                    Agregar trigger
                </button>
            )}
        </div>
    );
}

/* ─── Sub: card de un trigger (renderiza según type) ─── */
function TriggerCard({
    trigger,
    onChange,
    onRemove,
}: {
    trigger: Trigger;
    onChange: (patch: Partial<Trigger>) => void;
    onRemove: () => void;
}) {
    if (trigger.type === "event") {
        const def = EVENT_BY_KEY[trigger.eventKey];
        if (!def) return null;
        return (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-start gap-3">
                        <div className="text-2xl leading-none">
                            {def.emoji}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span
                                    className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-${CATEGORY_META[def.category].color}-100 text-${CATEGORY_META[def.category].color}-700`}
                                >
                                    Evento
                                </span>
                                <span className="text-[11px] text-slate-500">
                                    {CATEGORY_META[def.category].label}
                                </span>
                            </div>
                            <h4 className="font-bold text-sm text-slate-900">
                                {def.label}
                            </h4>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {def.description}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onRemove}
                        className="text-slate-400 hover:text-red-600 p-1"
                        aria-label="Quitar trigger"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>

                {/* Params editables */}
                {def.params && def.params.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {def.params.map((p) => (
                            <div key={p.key}>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                    {p.label}
                                    {p.required && (
                                        <span className="text-red-500 ml-0.5">
                                            *
                                        </span>
                                    )}
                                </label>
                                <div className="relative">
                                    <input
                                        type={
                                            p.type === "number"
                                                ? "number"
                                                : "text"
                                        }
                                        value={
                                            (trigger.params?.[p.key] as any) ??
                                            p.defaultValue ??
                                            ""
                                        }
                                        onChange={(e) => {
                                            const v =
                                                p.type === "number"
                                                    ? Number(e.target.value)
                                                    : e.target.value;
                                            onChange({
                                                params: {
                                                    ...(trigger as any).params,
                                                    [p.key]: v,
                                                },
                                            } as any);
                                        }}
                                        className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                                    />
                                    {p.suffix && (
                                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 pointer-events-none">
                                            {p.suffix}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (trigger.type === "time") {
        return (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3">
                        <div className="text-2xl">⏰</div>
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                    Tiempo
                                </span>
                            </div>
                            <h4 className="font-bold text-sm text-slate-900">
                                Trigger por tiempo
                            </h4>
                        </div>
                    </div>
                    <button
                        onClick={onRemove}
                        className="text-slate-400 hover:text-red-600 p-1"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>

                <div className="space-y-3">
                    {/* Mode picker */}
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                            Modo
                        </label>
                        <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                            {(
                                [
                                    { v: "fixed", l: "Fecha fija" },
                                    { v: "recurring", l: "Recurrente" },
                                    { v: "relative", l: "Relativo a evento" },
                                ] as const
                            ).map((m) => (
                                <button
                                    key={m.v}
                                    onClick={() =>
                                        onChange({ mode: m.v } as any)
                                    }
                                    className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                        trigger.mode === m.v
                                            ? "bg-violet-600 text-white shadow-sm"
                                            : "text-slate-600 hover:text-slate-900"
                                    }`}
                                >
                                    {m.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    {trigger.mode === "fixed" && (
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                Fecha y hora
                            </label>
                            <input
                                type="datetime-local"
                                value={
                                    trigger.scheduledFor
                                        ? trigger.scheduledFor.slice(0, 16)
                                        : ""
                                }
                                onChange={(e) =>
                                    onChange({
                                        scheduledFor: new Date(
                                            e.target.value,
                                        ).toISOString(),
                                    } as any)
                                }
                                className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                            />
                        </div>
                    )}

                    {trigger.mode === "recurring" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                    Días de la semana
                                </label>
                                <div className="flex gap-1">
                                    {["L", "M", "M", "J", "V", "S", "D"].map(
                                        (d, i) => {
                                            const day = i + 1;
                                            const selected = (
                                                trigger.daysOfWeek || []
                                            ).includes(day);
                                            return (
                                                <button
                                                    key={i}
                                                    onClick={() => {
                                                        const next =
                                                            selected
                                                                ? (
                                                                      trigger.daysOfWeek ||
                                                                      []
                                                                  ).filter(
                                                                      (x) =>
                                                                          x !==
                                                                          day,
                                                                  )
                                                                : [
                                                                      ...(trigger.daysOfWeek ||
                                                                          []),
                                                                      day,
                                                                  ];
                                                        onChange({
                                                            daysOfWeek: next,
                                                        } as any);
                                                    }}
                                                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${
                                                        selected
                                                            ? "bg-violet-600 text-white"
                                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                                    }`}
                                                >
                                                    {d}
                                                </button>
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                    Hora
                                </label>
                                <input
                                    type="time"
                                    value={trigger.timeOfDay || "10:00"}
                                    onChange={(e) =>
                                        onChange({
                                            timeOfDay: e.target.value,
                                        } as any)
                                    }
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                                />
                            </div>
                        </div>
                    )}

                    {trigger.mode === "relative" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                    Días después
                                </label>
                                <input
                                    type="number"
                                    value={trigger.daysOffset ?? 3}
                                    onChange={(e) =>
                                        onChange({
                                            daysOffset: Number(e.target.value),
                                        } as any)
                                    }
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                    Relativo a
                                </label>
                                <select
                                    value={trigger.relativeTo || "signup"}
                                    onChange={(e) =>
                                        onChange({
                                            relativeTo: e.target.value,
                                        } as any)
                                    }
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                                >
                                    {EVENT_CATALOG.filter(
                                        (e) => e.category === "lifecycle",
                                    ).map((e) => (
                                        <option key={e.key} value={e.key}>
                                            {e.emoji} {e.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (trigger.type === "whatsapp_message") {
        const cat = WA_MESSAGE_CATEGORIES.find(
            (c) => c.key === trigger.category,
        );
        return (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3">
                        <div className="text-2xl">{cat?.emoji || "💬"}</div>
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
                                    Mensaje WhatsApp
                                </span>
                            </div>
                            <h4 className="font-bold text-sm text-slate-900">
                                {cat?.label || "Categoría"}
                            </h4>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {cat?.description}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onRemove}
                        className="text-slate-400 hover:text-red-600 p-1"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>

                <div className="space-y-3 mt-3 pt-3 border-t border-slate-100">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                            Categoría detectada por IA
                        </label>
                        <select
                            value={trigger.category}
                            onChange={(e) =>
                                onChange({
                                    category: e.target
                                        .value as WhatsAppMessageCategory,
                                } as any)
                            }
                            className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                        >
                            {WA_MESSAGE_CATEGORIES.map((c) => (
                                <option key={c.key} value={c.key}>
                                    {c.emoji} {c.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                            Keywords adicionales{" "}
                            <span className="font-normal normal-case text-slate-400">
                                (opcional, separadas por coma)
                            </span>
                        </label>
                        <input
                            type="text"
                            value={(trigger.keywords || []).join(", ")}
                            onChange={(e) =>
                                onChange({
                                    keywords: e.target.value
                                        .split(",")
                                        .map((s) => s.trim())
                                        .filter(Boolean),
                                } as any)
                            }
                            placeholder={
                                cat?.defaultKeywords?.join(", ") || ""
                            }
                            className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                        />
                    </div>
                </div>
            </div>
        );
    }

    return null;
}

/* ─── Sub: sheet para elegir tipo + evento al agregar ─── */
function AddTriggerSheet({
    onAdd,
    onCancel,
}: {
    onAdd: (t: Trigger) => void;
    onCancel: () => void;
}) {
    const [pickedType, setPickedType] = useState<
        "event" | "time" | "whatsapp_message" | null
    >(null);
    const [eventCategory, setEventCategory] = useState<EventCategory | null>(
        null,
    );
    // Time trigger config
    const [timeMode, setTimeMode] = useState<"recurring" | "fixed">("recurring");
    const [timeDays, setTimeDays] = useState<number[]>([1, 2, 3, 4, 5]);
    const [timeHour, setTimeHour] = useState("10:00");
    // WA trigger config
    const [waCategory, setWaCategory] = useState<WhatsAppMessageCategory>("inquiry");
    const [waKeywords, setWaKeywords] = useState("");

    const newId = () => "trg-" + Math.random().toString(36).slice(2, 10);

    return (
        <div className="bg-white rounded-2xl border-2 border-violet-300 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-violet-100 bg-violet-50 flex items-center justify-between">
                <h4 className="text-sm font-bold text-violet-900">
                    {!pickedType
                        ? "¿Qué tipo de trigger?"
                        : pickedType === "event"
                          ? eventCategory
                              ? `Eventos: ${CATEGORY_META[eventCategory].label}`
                              : "Elegí categoría de evento"
                          : pickedType === "time"
                            ? "Configurá el trigger por tiempo"
                            : "Configurá el trigger por mensaje WhatsApp"}
                </h4>
                <button
                    onClick={onCancel}
                    className="text-xs text-violet-700 hover:underline"
                >
                    Cancelar
                </button>
            </div>
            <div className="p-5">
                {/* Step 1: type selection */}
                {!pickedType && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <TypeButton
                            icon="📊"
                            label="Por evento"
                            description="Comportamiento del cliente, lifecycle, catálogo"
                            onClick={() => setPickedType("event")}
                        />
                        <TypeButton
                            icon="⏰"
                            label="Por tiempo"
                            description="Fecha fija, recurrente, o relativo a otro evento"
                            onClick={() => setPickedType("time")}
                        />
                        <TypeButton
                            icon="💬"
                            label="Por mensaje WhatsApp"
                            description="Detecta intent en mensajes entrantes (queja, consulta, etc.)"
                            onClick={() => setPickedType("whatsapp_message")}
                        />
                    </div>
                )}

                {/* Step 2 (time): configure time trigger */}
                {pickedType === "time" && (
                    <div className="space-y-4">
                        <button
                            onClick={() => setPickedType(null)}
                            className="text-xs text-violet-700 hover:underline"
                        >
                            ← Volver a tipos
                        </button>
                        {/* Mode */}
                        <div>
                            <label className="text-xs font-semibold text-slate-700 block mb-2">
                                Modalidad
                            </label>
                            <div className="flex gap-2">
                                {(["recurring", "fixed"] as const).map((m) => (
                                    <button
                                        key={m}
                                        onClick={() => setTimeMode(m)}
                                        className={`flex-1 py-2 rounded-xl border-2 text-xs font-semibold transition-all ${
                                            timeMode === m
                                                ? "border-violet-400 bg-violet-50 text-violet-700"
                                                : "border-slate-200 text-slate-600 hover:border-slate-300"
                                        }`}
                                    >
                                        {m === "recurring"
                                            ? "⟳ Recurrente"
                                            : "📅 Fecha fija"}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* Days of week (recurring only) */}
                        {timeMode === "recurring" && (
                            <div>
                                <label className="text-xs font-semibold text-slate-700 block mb-2">
                                    Días
                                </label>
                                <div className="flex gap-1.5 flex-wrap">
                                    {[
                                        { d: 1, label: "Lun" },
                                        { d: 2, label: "Mar" },
                                        { d: 3, label: "Mié" },
                                        { d: 4, label: "Jue" },
                                        { d: 5, label: "Vie" },
                                        { d: 6, label: "Sáb" },
                                        { d: 0, label: "Dom" },
                                    ].map(({ d, label }) => {
                                        const on = timeDays.includes(d);
                                        return (
                                            <button
                                                key={d}
                                                onClick={() =>
                                                    setTimeDays((prev) =>
                                                        on
                                                            ? prev.filter(
                                                                  (x) =>
                                                                      x !== d,
                                                              )
                                                            : [...prev, d],
                                                    )
                                                }
                                                className={`w-10 h-8 rounded-lg text-xs font-bold transition-all ${
                                                    on
                                                        ? "bg-violet-600 text-white"
                                                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {/* Hour */}
                        <div>
                            <label className="text-xs font-semibold text-slate-700 block mb-1">
                                Hora
                            </label>
                            <input
                                type="time"
                                value={timeHour}
                                onChange={(e) => setTimeHour(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                            />
                        </div>
                        {/* Confirm */}
                        <button
                            onClick={() =>
                                onAdd({
                                    id: newId(),
                                    type: "time",
                                    mode: timeMode,
                                    ...(timeMode === "recurring"
                                        ? {
                                              daysOfWeek: timeDays,
                                              timeOfDay: timeHour,
                                          }
                                        : { timeOfDay: timeHour }),
                                })
                            }
                            className="w-full py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold rounded-xl transition-colors"
                        >
                            Confirmar trigger
                        </button>
                    </div>
                )}

                {/* Step 2 (whatsapp_message): configure WA trigger */}
                {pickedType === "whatsapp_message" && (
                    <div className="space-y-4">
                        <button
                            onClick={() => setPickedType(null)}
                            className="text-xs text-violet-700 hover:underline"
                        >
                            ← Volver a tipos
                        </button>
                        {/* Category */}
                        <div>
                            <label className="text-xs font-semibold text-slate-700 block mb-2">
                                Categoría del mensaje
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {WA_MESSAGE_CATEGORIES.map((cat) => (
                                    <button
                                        key={cat.key}
                                        onClick={() =>
                                            setWaCategory(
                                                cat.key as WhatsAppMessageCategory,
                                            )
                                        }
                                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                                            waCategory === cat.key
                                                ? "border-violet-400 bg-violet-50"
                                                : "border-slate-200 hover:border-slate-300"
                                        }`}
                                    >
                                        <div className="text-lg mb-0.5">
                                            {cat.emoji}
                                        </div>
                                        <div className="text-xs font-bold text-slate-900">
                                            {cat.label}
                                        </div>
                                        <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">
                                            {cat.description}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* Keywords */}
                        <div>
                            <label className="text-xs font-semibold text-slate-700 block mb-1">
                                Palabras clave{" "}
                                <span className="text-slate-400 font-normal">
                                    (opcional, separadas por coma)
                                </span>
                            </label>
                            <input
                                type="text"
                                value={waKeywords}
                                onChange={(e) => setWaKeywords(e.target.value)}
                                placeholder="cancelar, baja, no quiero..."
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                            />
                        </div>
                        {/* Confirm */}
                        <button
                            onClick={() =>
                                onAdd({
                                    id: newId(),
                                    type: "whatsapp_message",
                                    category: waCategory,
                                    ...(waKeywords.trim()
                                        ? {
                                              keywords: waKeywords
                                                  .split(",")
                                                  .map((k) => k.trim())
                                                  .filter(Boolean),
                                          }
                                        : {}),
                                })
                            }
                            className="w-full py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold rounded-xl transition-colors"
                        >
                            Confirmar trigger
                        </button>
                    </div>
                )}

                {/* Step 2 (only for event): pick category then event */}
                {pickedType === "event" && !eventCategory && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {(Object.keys(CATEGORY_META) as EventCategory[]).map(
                            (cat) => {
                                const meta = CATEGORY_META[cat];
                                const count = EVENTS_BY_CATEGORY[cat].length;
                                return (
                                    <button
                                        key={cat}
                                        onClick={() => setEventCategory(cat)}
                                        className="p-4 rounded-xl border-2 border-slate-200 hover:border-violet-400 hover:bg-violet-50/40 text-left transition-all"
                                    >
                                        <div className="text-2xl mb-1">
                                            {meta.emoji}
                                        </div>
                                        <h5 className="text-sm font-bold text-slate-900">
                                            {meta.label}
                                        </h5>
                                        <p className="text-[10px] text-slate-500 mt-0.5">
                                            {count} eventos
                                        </p>
                                    </button>
                                );
                            },
                        )}
                    </div>
                )}

                {pickedType === "event" && eventCategory && (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        <button
                            onClick={() => setEventCategory(null)}
                            className="text-xs text-violet-700 hover:underline mb-2"
                        >
                            ← Volver a categorías
                        </button>
                        {EVENTS_BY_CATEGORY[eventCategory].map((e) => (
                            <button
                                key={e.key}
                                onClick={() => {
                                    const defaults: Record<string, any> = {};
                                    e.params?.forEach((p) => {
                                        if (p.defaultValue !== undefined)
                                            defaults[p.key] = p.defaultValue;
                                    });
                                    onAdd({
                                        id: newId(),
                                        type: "event",
                                        eventKey: e.key,
                                        params: Object.keys(defaults).length
                                            ? defaults
                                            : undefined,
                                    });
                                }}
                                className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-violet-400 hover:bg-violet-50/40 text-left transition-all"
                            >
                                <div className="text-xl flex-shrink-0">
                                    {e.emoji}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h5 className="text-sm font-bold text-slate-900">
                                        {e.label}
                                    </h5>
                                    <p className="text-[11px] text-slate-500 line-clamp-1">
                                        {e.description}
                                    </p>
                                </div>
                                <ChevronRight
                                    size={16}
                                    className="text-slate-400 flex-shrink-0"
                                />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function TypeButton({
    icon,
    label,
    description,
    onClick,
}: {
    icon: string;
    label: string;
    description: string;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            className="p-4 rounded-xl border-2 border-slate-200 hover:border-violet-400 hover:bg-violet-50/40 text-left transition-all"
        >
            <div className="text-2xl mb-1.5">{icon}</div>
            <h5 className="text-sm font-bold text-slate-900">{label}</h5>
            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                {description}
            </p>
        </button>
    );
}
